import React, { createContext } from 'react';
import { useImmerReducer } from 'use-immer';
import { ActionT, LayoutSettings, WidgetUI } from '@/types';
import { organizationConfig, whiteLabelingOrganisations } from '@/context/AdminLayoutContext';
import { useMediaQuery } from 'react-responsive';
import { useReactiveVar } from '@apollo/client';
import { currentUserVar } from '@/graphql/operations/user';

export interface BulkApplyShift {
  id: string;
  startTime: string;
  endTime: string;
}

export interface IFlexUserLayoutContext {
  // props
  isMobile?: boolean;
  layoutConfig: LayoutSettings;
  showWidgetPopup?: boolean;
  widgetsMaskVisible?: boolean;
  currentActiveWidget?: WidgetUI;
  currentCapacityShiftType?: string | undefined;
  selectedShifts: BulkApplyShift[];

  // methods
  toggleWidgetPopup?: () => void;
  toggleWidgetMask?: () => void;
  setCurrentActiveWidget?: (value: WidgetUI) => void;
  setCurrentCapacityShiftType?: (type: string) => void;
  setSelectedShifts?: (shifts: BulkApplyShift[]) => void;
}

const FlexUserLayoutContext = createContext<IFlexUserLayoutContext>({
  layoutConfig: organizationConfig['ARYA'],
  selectedShifts: [],
});

type State = {
  showWidgetPopup: boolean;
  currentActiveWidget?: WidgetUI;
  widgetsMaskVisible: boolean;
  currentCapacityShiftType?: string;
  selectedShifts: BulkApplyShift[];
};

type Action =
  | ActionT<'toggleWidgetPopup', undefined>
  | ActionT<'setCurrentActiveWidget', State['currentActiveWidget']>
  | ActionT<'toggleWidgetMask', undefined>
  | ActionT<'setCurrentCapacityShiftType', State['currentCapacityShiftType']>
  | ActionT<'SET_SELECTED_SHIFTS', State['selectedShifts']>;

function reducer(draft: State, action: Action) {
  switch (action.type) {
    case 'toggleWidgetPopup':
      draft.showWidgetPopup = !draft.showWidgetPopup;
      return;
    case 'setCurrentActiveWidget':
      draft.currentActiveWidget = action.payload;
      return;
    case 'toggleWidgetMask':
      draft.widgetsMaskVisible = !draft.widgetsMaskVisible;
      return;
    case 'SET_SELECTED_SHIFTS': {
      if (action.payload.length === 0) {
        draft.selectedShifts = [];
      }
      if (action.payload.length === 1) {
        action.payload.forEach((shift) => {
          const idx = draft.selectedShifts.findIndex((s) => s.id === shift.id);
          if (idx >= 0) {
            draft.selectedShifts.splice(idx, 1);
          } else {
            draft.selectedShifts.push(shift);
          }
        });
      } else {
        draft.selectedShifts = action.payload;
      }
      return;
    }
  }
}

type FlexUserLayoutProviderProps = React.PropsWithChildren;

export const FlexUserLayoutProvider: React.FC<FlexUserLayoutProviderProps> = ({ children }) => {
  const initialState: State = {
    showWidgetPopup: false,
    widgetsMaskVisible: false,
    selectedShifts: [],
  };

  const [state, dispatch] = useImmerReducer(reducer, initialState);

  const toggleWidgetPopup = () => {
    dispatch({ type: 'toggleWidgetPopup', payload: undefined });
  };

  const toggleWidgetMask = () => {
    dispatch({ type: 'toggleWidgetMask', payload: undefined });
  };

  const setCurrentActiveWidget = (value: WidgetUI) => {
    dispatch({ type: 'setCurrentActiveWidget', payload: value });
  };

  const setCurrentCapacityShiftType = (type: string) => {
    dispatch({ type: 'setCurrentCapacityShiftType', payload: type });
  };

  const setSelectedShifts = (shifts: BulkApplyShift[]) => {
    dispatch({ type: 'SET_SELECTED_SHIFTS', payload: shifts });
  };

  const currentUser = useReactiveVar(currentUserVar);
  const orgName = currentUser.organization.name as 'ARYA';
  const isMobile = useMediaQuery({ query: '(max-width: 900px)' });

  return (
    <FlexUserLayoutContext.Provider
      value={{
        isMobile,
        layoutConfig: !whiteLabelingOrganisations.includes(orgName)
          ? organizationConfig['ARYA']
          : organizationConfig[orgName],
        ...state,
        toggleWidgetPopup,
        setCurrentActiveWidget,
        toggleWidgetMask,
        setCurrentCapacityShiftType,
        setSelectedShifts,
      }}
    >
      {children}
    </FlexUserLayoutContext.Provider>
  );
};

export default FlexUserLayoutContext;
